import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Action } from '@atlassian/react-sweet-state';
import type {
	ObjectActivityTimelineState,
	ContainerProps,
} from '../../../../common/types/store.tsx';
import {
	toTimelineEventsServiceError,
	type TimelineEventType,
} from '../../../../common/types/timeline-event.tsx';

import { handleTimelineNetworkError } from '../../../../common/utils/index.tsx';
import {
	LOGGER_LOCATION_FETCH_ACTIVITY,
	fetchObjectActivity as fetchObjectActivityRequest,
} from '../../../../services/object-activity/index.tsx';
import type { ObjectActivityType } from '../../../../services/object-activity/types.tsx';
import { transformResponse } from '../../../../services/object-activity/utils.tsx';
import { getServiceEventNameByStoreEvent } from '../../../utils/index.tsx';

export const PAGE_SIZE = 25;

type ActionPayload = {
	/**
	 * true: fetches the next page of data (if it exists) and appends to existing data.
	 *
	 * false: fetches the first page of data, then replaces any existing events
	 */
	fetchNextPage: boolean;
};

const defaultPayload: ActionPayload = {
	fetchNextPage: false,
};

export const fetchObjectActivity =
	({ fetchNextPage }: ActionPayload = defaultPayload): Action<
		ObjectActivityTimelineState,
		ContainerProps
	> =>
	async ({ setState, getState }, { workspaceId, objectId, createAnalyticsEvent }) => {
		const existingState = getState();

		if (existingState.loading) {
			return;
		}

		if (fetchNextPage && existingState.page.isLast) {
			return;
		}

		setState({ loading: true });

		try {
			const serviceResponse = await fetchObjectActivityRequest({
				workspaceId,
				objectId,
				activityTypes: existingState.filteredByEventTypes
					.map((type: TimelineEventType) => getServiceEventNameByStoreEvent(type))
					.filter((objType): objType is ObjectActivityType => !!objType),
				orderAsc: false,
				startAt: fetchNextPage ? existingState.page.current * PAGE_SIZE : 0,
				maxResults: PAGE_SIZE,
			});

			const timelineEvents = transformResponse(serviceResponse);

			const newEvents = fetchNextPage
				? [...(existingState.timelineEvents ?? []), ...timelineEvents]
				: timelineEvents;

			setState({
				loading: false,
				timelineEvents: newEvents,
				page: {
					current: (fetchNextPage ? existingState.page.current : 0) + 1,
					isLast: serviceResponse.isLast,
				},
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectHistory succeeded', {
				isFirstPage: !fetchNextPage,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (error.name === 'AbortError') {
				return;
			}

			const handledError = handleTimelineNetworkError(error, LOGGER_LOCATION_FETCH_ACTIVITY);

			setState({ error: toTimelineEventsServiceError(handledError.error), loading: false });

			fireErrorAnalytics({
				error: handledError.rawError,
				meta: {
					id: 'fetchObjectHistory',
					packageName: 'jiraServicedeskInsightObjectActivityTimelineStore',
					teamName: 'falcons',
				},
				attributes: {
					errorType: handledError.error,
					errorMessage: handledError.rawError.message,
					isFirstPage: !fetchNextPage,
				},
			});
		}
	};
